import { createTikTokStyleCaptions } from '@remotion/captions';
import React, { useContext, useMemo } from 'react';
import {
  Sequence,
  useCurrentFrame,
  useRemotionEnvironment,
  useVideoConfig,
} from 'remotion';
import { CaptionAsset } from '../../assets/assets';
import { calculateCaptionPageTimingFrames } from './caption-page-sync-lead';
import {
  CAPTION_GAP_SPLIT_THRESHOLD_MS,
  splitCaptionsByGap,
} from './split-captions-by-gap';
import { TextItemHoverPreviewContext } from '../../context-provider';
import { FontInfoContext } from '../../utils/text/font-info';
import { resolveFontFamilyCssStack } from '../../utils/text/font-family-css-stack';
import { useLoadFontFromTextItem } from '../../utils/text/load-font-from-text-item';
import { useAssetFromAssetId } from '../../utils/use-context';
import {
  calculateAnimationsStyle,
  mergeAnimationStyle,
} from '../animation/calculate-animation';
import { EditorStarterItem } from '../item-type';
import {
  defaultTextShadow,
  defaultTextItemBackground,
  TextShadow,
  TextItemBackground,
  BackgroundRenderableItem,
} from '../text/text-item-type';
import {
  calculateFadeInOpacity,
  calculateFadeOutOpacity,
} from '../video/calculate-fade';
import { CaptionPage } from './caption-page';
import { overrideCaptionsItemWithHoverPreview } from './override-captions-item-with-hover-preview';
import { TextBackgroundRenderer } from '../text/TextBackgroundRenderer';
import type { CaptionPresentationRenderData } from './caption-presentation-merge';
import { captionEdgeFrames } from '@waka/compat/caption-edge-timing';
import {projectCaptionViewWindow} from '@waka/compat/caption-view-window';

export const CaptionsLayer = ({
  item: itemWithoutHoverPreview,
  zIndex,
  presentationData,
}: {
  item: EditorStarterItem;
  zIndex?: number;
  presentationData?: CaptionPresentationRenderData;
}) => {
  if (itemWithoutHoverPreview.type !== 'captions') {
    throw new Error('Item is not captions');
  }

  const textItemHoverPreview = useContext(TextItemHoverPreviewContext);
  const item = useMemo(() => {
    return overrideCaptionsItemWithHoverPreview(
      itemWithoutHoverPreview,
      textItemHoverPreview,
    );
  }, [itemWithoutHoverPreview, textItemHoverPreview]);

  const captionAsset = useAssetFromAssetId(item.assetId) as CaptionAsset;
  const context = useContext(FontInfoContext);

  const shadow = useMemo<TextShadow>(() => {
    return {
      ...defaultTextShadow,
      ...(item.shadow ?? {}),
    };
  }, [item.shadow]);

  const background = useMemo<TextItemBackground>(() => {
    return {
      ...defaultTextItemBackground,
      ...(item.background ?? {}),
    };
  }, [item.background]);

  const loaded = useLoadFontFromTextItem({
    fontFamily: item.fontFamily,
    fontVariant: item.fontStyle.variant,
    fontWeight: item.fontStyle.weight,
    fontInfosDuringRendering: context[item.fontFamily] ?? null,
  });

  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const { isPlayer } = useRemotionEnvironment();
  const cssFontFamily = resolveFontFamilyCssStack(item.fontFamily);

  const opacity = useMemo(() => {
    const inOpacity = calculateFadeInOpacity({
      fadeWindow:item.visualFadeWindow,
      currentFrame: frame,
      fadeInDurationInSeconds: item.fadeInDurationInSeconds,
      framesPerSecond: fps,
    });
    const outOpacity = calculateFadeOutOpacity({
      fadeWindow:item.visualFadeWindow,
      currentFrame: frame,
      fadeOutDurationInSeconds: item.fadeOutDurationInSeconds,
      framesPerSecond: fps,
      totalDurationInFrames: item.durationInFrames,
    });
    return inOpacity * outOpacity * item.opacity;
  }, [
    item.visualFadeWindow,
    item.fadeInDurationInSeconds,
    fps,
    frame,
    item.opacity,
    item.durationInFrames,
    item.fadeOutDurationInSeconds,
  ]);

  const style: React.CSSProperties = useMemo(() => {
    const hasRotation = item.rotation !== 0;

    return {
      position: 'absolute',
      left: item.left,
      top: item.top,
      width: item.width,
      height: item.height,
      fontFamily: cssFontFamily,
      opacity: opacity,
      transform: hasRotation ? `rotate(${item.rotation}deg)` : undefined,
      zIndex,
      isolation: zIndex !== undefined ? 'isolate' : undefined,
    };
  }, [cssFontFamily, item, opacity, zIndex]);

  // Animation 스타일 계산
  const animationStyle = useMemo(() => {
    const hasAnimations = item.animations && item.animations.length > 0;
    const hasPositionKeyframes =
      item.positionKeyframes && item.positionKeyframes.length > 0;
    if (!hasAnimations && !hasPositionKeyframes) {
      return {};
    }
    return calculateAnimationsStyle({
      animations: item.animations ?? [],
      currentFrame: frame,
      framesPerSecond: fps,
      totalDurationInFrames: item.durationInFrames,
      positionKeyframes: item.positionKeyframes,
    });
  }, [
    item.animations,
    item.positionKeyframes,
    frame,
    fps,
    item.durationInFrames,
  ]);

  // style에 animation 적용
  const finalStyle = useMemo(() => {
    return mergeAnimationStyle(style, animationStyle);
  }, [style, animationStyle]);

  const pages = useMemo(() => {
    // Presentation data already represents a merged timeline view; preserve its
    // grouping and clock. Standalone source-backed views need their own cut.
    const captions = presentationData?.captions ??
      projectCaptionViewWindow(captionAsset.captions, {
        timelineSourceAssetId: captionAsset.timelineSourceAssetId,
        captionStartInSeconds: item.captionStartInSeconds,
        durationInFrames: item.durationInFrames,
        fps,
        captionEdgeHold: item.captionEdgeHold,
      }).map(({caption}) => caption);
    const groups = splitCaptionsByGap(
      captions,
      CAPTION_GAP_SPLIT_THRESHOLD_MS,
    );
    return groups.flatMap(
      (group) =>
        createTikTokStyleCaptions({
          captions: group,
          combineTokensWithinMilliseconds: item.pageDurationInMilliseconds,
        }).pages,
    );
  }, [
    captionAsset.captions,
    captionAsset.timelineSourceAssetId,
    fps,
    item.captionStartInSeconds,
    item.durationInFrames,
    item.captionEdgeHold,
    item.pageDurationInMilliseconds,
    presentationData?.captions,
  ]);

  const captionDisplayEndMs = useMemo(() => {
    if (presentationData) {
      return presentationData.displayEndMs;
    }

    if (captionAsset.timelineSourceAssetId && fps > 0) {
      return (item.captionStartInSeconds + item.durationInFrames / fps) * 1000;
    }

    const itemEndMs = ((item.from + item.durationInFrames) / fps) * 1000;
    const finalWordEndMs = captionAsset.captions.reduce(
      (latestEndMs, caption) =>
        Number.isFinite(caption.endMs)
          ? Math.max(latestEndMs, caption.endMs)
          : latestEndMs,
      0,
    );

    const explicitDisplayEndCandidates = [
      item.renderMeta?.videoEnd,
      captionAsset.videoEnd,
    ].filter(
      (candidate): candidate is number =>
        typeof candidate === 'number' &&
        Number.isFinite(candidate) &&
        candidate >= finalWordEndMs,
    );
    const explicitDisplayEndMs =
      explicitDisplayEndCandidates.length > 0
        ? Math.max(...explicitDisplayEndCandidates)
        : null;

    return explicitDisplayEndMs ?? Math.max(finalWordEndMs, itemEndMs);
  }, [
    captionAsset.captions,
    captionAsset.videoEnd,
    captionAsset.timelineSourceAssetId,
    fps,
    item,
    presentationData,
  ]);

  // full-area 모드: 전체 캡션 영역에 배경 렌더링 (한 번만 렌더링)
  const fullAreaBackgroundItem: BackgroundRenderableItem = useMemo(
    () => ({
      text: '', // full-area 모드에서는 텍스트 측정이 필요 없음
      left: item.left,
      top: item.top,
      width: item.width,
      height: item.height,
      fontSize: item.fontSize,
      fontFamily: cssFontFamily,
      fontStyle: item.fontStyle,
      lineHeight: item.lineHeight,
      letterSpacing: item.letterSpacing,
      align: item.align,
      rotation: item.rotation,
      background: item.background,
    }),
    [cssFontFamily, item],
  );

  // text-only 모드: 각 페이지의 텍스트에 맞춰 배경 렌더링
  const isBackgroundVisible =
    background.visible && item.backgroundVisible !== false;
  const isTextOnlyMode = isBackgroundVisible && background.mode === 'text-only';
  const effectiveColor =
    item.colorVisible === false ? 'transparent' : item.color;
  const effectiveHighlightColor =
    item.highlightVisible === false ? null : (item.highlightColor ?? null);
  const effectiveStrokeColor =
    item.strokeVisible === false ? 'transparent' : item.strokeColor;

  if (!loaded) {
    return null;
  }

  return (
    <>
      {/*
       * Full-area 모드: 캡션 박스 전체 영역에 단일 배경 렌더링
       *
       * - Full-area 모드는 캡션 텍스트와 무관하게 item의 전체 영역(left, top, width, height)에
       *   배경을 한 번만 렌더링하므로, 페이지가 바뀌어도 배경이 깜빡이지 않음
       * - TextBackgroundRenderer의 full-area 로직을 재사용하여 일관성 유지
       * - zIndex를 -1로 설정하여 텍스트 뒤에 배경이 위치하도록 함
       *
       * cf. Text-only 모드는 각 페이지의 실제 텍스트 영역에 맞춰 배경을 렌더링해야 하므로
       *     CaptionPage 컴포넌트 내부에서 줄별로 측정 후 처리함 (caption-page.tsx 참고)
       */}
      {isBackgroundVisible && background.mode === 'full-area' && (
        <TextBackgroundRenderer
          item={fullAreaBackgroundItem}
          opacity={opacity}
          zIndex={zIndex !== undefined ? zIndex - 1 : undefined}
        />
      )}
      <div style={finalStyle} className="select-none">
        {pages.map((page, index) => {
          const nextPage = pages[index + 1] ?? null;
          // asset.captions 의 startMs/endMs 는 원본 영상 절대 ms 를 유지하고,
          // item.captionStartInSeconds 가 타임라인에서 사용자가 옮긴 offset(초) 역할을 한다.
          const captionOffsetInSeconds =
            presentationData?.captionStartInSeconds ??
            item.captionStartInSeconds;
          const previousPageEndMs = pages
            .slice(0, index)
            .reduce(
              (latestEndMs, previousPage) =>
                Math.max(
                  latestEndMs,
                  previousPage.startMs + previousPage.durationMs,
                ),
              0,
            );
          const {
            startFrame: subtitleStartFrame,
            durationInFrames,
            syncLeadMs,
          } = captionEdgeFrames(
            calculateCaptionPageTimingFrames({
              page,
              nextPage,
              fps,
              captionOffsetInSeconds,
              previousPageEndMs,
              // Legacy payloads do not have captionSyncLeadMs. Keeping zero as
              // the default preserves their original sequence and word timing,
              // including when the desktop alias advances before the web rollout.
              maxSyncLeadMs: item.captionSyncLeadMs ?? 0,
              displayEndMs: captionDisplayEndMs,
            }),
            index,
            pages.length,
            item.durationInFrames,
            fps,
            item.captionEdgeHold,
          );
          // offset 을 빼 준 프레임 범위가 0 이하라면 실제로 표시할 구간이 없으므로 렌더를 건너뛴다.
          if (durationInFrames <= 0) {
            return null;
          }

          return (
            <Sequence
              key={index}
              from={subtitleStartFrame}
              durationInFrames={durationInFrames}
            >
              <CaptionPage
                sourceTypography={item.sourceTypography}
                captionWidth={item.width}
                captionHeight={item.height}
                fontFamily={item.fontFamily}
                fontStyle={item.fontStyle}
                key={index}
                page={page}
                presentationLeadMs={syncLeadMs}
                sequenceStartMs={
                  ((item.from + subtitleStartFrame) / fps) * 1000
                }
                usePresentedMediaClock={isPlayer}
                preserveWordEndTiming={(item.captionTimingPolicyVersion ?? 1) >= 2}
                lineHeight={item.lineHeight}
                letterSpacing={item.letterSpacing}
                color={effectiveColor}
                direction={item.direction}
                align={item.align}
                fontSize={item.fontSize}
                highlightColor={effectiveHighlightColor}
                highlightMode={item.highlightMode}
                highlightEffect={item.highlightEffect}
                maxLines={item.maxLines}
                shadow={shadow}
                shadowVisible={item.shadowVisible !== false}
                strokeWidth={item.strokeWidth}
                strokeColor={effectiveStrokeColor}
                background={isTextOnlyMode ? item.background : undefined}
                backgroundOpacity={opacity}
              />
            </Sequence>
          );
        })}
      </div>
    </>
  );
};
